import Vue from 'vue'
import i18n from '@/plugins/i18n'
import { MYME_TYPES } from '@/constants/settings.js'

const getErrorDescription = (error) => {
  if (!error.response) {
    return {
      message: error.message
        ? i18n.tc(error.message)
        : i18n.tc('Непредвиденная ошибка, обратитесь к администратору')
    }
  }
  const status = parseInt(error.response.status)
  let description = null
  if (error.response &&
    error.response.data &&
    error.response.data.description) {
    description = error.response.data.description
    if (description && Vue.getEnvironmentName() === 'development') {
      // eslint-disable-next-line no-console
      console.error(description)
    }
  }
  if (error.response &&
    error.response.data &&
    error.response.data.message) {
    const errorInfo = {
      ...error.response.data,
      description: status === 422 || status === 500
        ? null
        : description
    }
    return errorInfo
  }
  // если в запросе ожидался файл, но пришло описание ошибки в JSON
  if (error.request && error.request.responseType === MYME_TYPES.ARRAYBUFFER) {
    try {
      error = JSON.parse(Buffer.from(error.response.data).toString('utf8'))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error.message)
    }
  }

  switch (status) {
    case 422:
    case 500:
      error = {
        message: error.message
          ? error.message
          : i18n.tc('Непредвиденная ошибка на сервере'),
        description
      }
      break
    case 403:
      error = {
        message: i18n.tc('У клиента нет прав доступа к содержимому, поэтому сервер отказывается дать надлежащий ответ.'),
        description
      }
      break
    case 400 :
      error = {
        message: i18n.tc('Сервер не понимает запрос из-за неверного синтаксиса'),
        description
      }
      break
    case 401 :
      error = {
        message: i18n.tc('Для получения запрашиваемого ответа нужна аутентификация'),
        description
      }
      break
    case 404 :
      error = {
        message: i18n.tc('Сервер не может найти запрашиваемый ресурс.'),
        description
      }
      break
    default:
      error = {
        message: error.message
          ? error.message
          : i18n.tc('Непредвиденная ошибка, обратитесь к администратору'),
        description
      }
  }
  return error
}

const resolveErrorMessage = (err, defaultMessage) => {
  const error = {}
  if (typeof (err) === 'string') {
    error.message = err
  } else if (typeof (err) === 'object' && err.message) {
    error.message = err.message
  } else {
    error.message = defaultMessage
  }
  return error
}

const downloadFile = (data, filename, mime) => {
  const blob = new Blob([data], { type: mime || MYME_TYPES.OCTET_STREAM })
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    window.navigator.msSaveBlob(blob, filename)
  } else {
    const blobURL = window.URL.createObjectURL(blob)
    const tempLink = document.createElement('a')
    tempLink.style.display = 'none'
    tempLink.href = blobURL
    tempLink.setAttribute('download', filename)

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank')
    }

    document.body.appendChild(tempLink)
    tempLink.click()
    document.body.removeChild(tempLink)
    window.URL.revokeObjectURL(blobURL)
  }
}

const getFileName = (resp) => {
  const disposition = resp.headers['content-disposition']
  const regex = /filename[*=]*UTF-8[']*(.*)[;]?/
  const matches = regex.exec(disposition)
  let filename = ''

  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, '')
  }

  return decodeURI(filename)
}

const excludeObjectProps = (obj, excludePropsArray = []) => {
  const result = { ...obj }
  excludePropsArray.forEach(element => {
    if (result.hasOwnProperty(element)) {
      delete result[element]
    }
  })
  return result
}

const getUrlString = (params, keys = [], isArray = false) => {
  const p = Object.keys(params).map(key => {
    let val = params[key]

    if (Object.prototype.toString.call(val) === '[object Object]' || Array.isArray(val)) {
      keys.push(Array.isArray(params) ? '' : key)

      return getUrlString(val, keys, Array.isArray(val))
    } else {
      let tKey = key

      if (keys.length > 0) {
        const tKeys = isArray
          ? keys
          : [...keys, key]
        tKey = tKeys.reduce((str, k) => {
          return str === ''
            ? k
            : `${str}[${k}]`
        },
        '')
      }
      val = typeof val === 'undefined' || val === null || val === 'null'
        ? ''
        : val
      return isArray
        ? `${tKey}[]=${val}`
        : `${tKey}=${val}`
    }
  }).filter(s => !!s).join('&')

  keys.pop()
  return p
}

const getQueryParamsString = (params, keys = [], isArray = false) => {
  const str = params
    ? `${getUrlString(params, keys, isArray)}`
    : ''
  return `${str}${str ? '&' : ''}timestamp=${new Date().getTime()}`
}

const customHelpers = {
  getErrorDescription,
  resolveErrorMessage,
  downloadFile,
  excludeObjectProps,
  getFileName,
  getQueryParamsString
}

Vue.helpers = Vue.prototype.$helpers = {
  ...Vue.helpers,
  ...customHelpers
}

export default customHelpers
